/**
 * Money Green Theme for Budget Flow
 *
 * A fresh, finance-focused theme using green tones to emphasize
 * the "money" aspect of budgeting. Green conveys growth, prosperity,
 * and financial health.
 */

// oxlint-disable-next-line eslint/no-restricted-imports
import * as colorPalette from '@desktop-client/style/palette';

// Custom Money Green palette extensions
const moneyGreen = {
  50: '#f0fdf4',
  100: '#dcfce7',
  150: '#bbf7d0',
  200: '#86efac',
  300: '#4ade80',
  400: '#22c55e',
  500: '#16a34a',
  600: '#15803d',
  700: '#166534',
  800: '#14532d',
  900: '#052e16',
};

const moneyTeal = {
  50: '#f0fdfa',
  100: '#ccfbf1',
  200: '#99f6e4',
  300: '#5eead4',
  400: '#2dd4bf',
  500: '#14b8a6',
  600: '#0d9488',
  700: '#0f766e',
  800: '#115e59',
  900: '#134e4a',
};

// Page backgrounds - light green tint
export const pageBackground = moneyGreen[50];
export const pageBackgroundModalActive = moneyGreen[100];
export const pageBackgroundTopLeft = moneyGreen[50];
export const pageBackgroundBottomRight = moneyGreen[100];
export const pageBackgroundLineTop = colorPalette.white;
export const pageBackgroundLineMid = moneyGreen[50];
export const pageBackgroundLineBottom = moneyGreen[100];
export const pageText = '#1a2e1a';
export const pageTextLight = moneyGreen[700];
export const pageTextSubdued = moneyGreen[400];
export const pageTextDark = moneyGreen[900];
export const pageTextPositive = moneyGreen[600];
export const pageTextLink = moneyTeal[600];
export const pageTextLinkLight = moneyTeal[400];

// Cards
export const cardBackground = colorPalette.white;
export const cardBorder = moneyGreen[600];
export const cardShadow = moneyGreen[800];

// Tables
export const tableBackground = colorPalette.white;
export const tableRowBackgroundHover = moneyGreen[50];
export const tableText = pageText;
export const tableTextLight = moneyGreen[600];
export const tableTextSubdued = moneyGreen[200];
export const tableTextSelected = moneyGreen[800];
export const tableTextHover = moneyGreen[900];
export const tableTextInactive = moneyGreen[500];
export const tableHeaderText = moneyGreen[700];
export const tableHeaderBackground = colorPalette.white;
export const tableBorder = moneyGreen[100];
export const tableBorderSelected = moneyGreen[500];
export const tableBorderHover = moneyGreen[400];
export const tableBorderSeparator = moneyGreen[400];
export const tableRowBackgroundHighlight = moneyGreen[100];
export const tableRowBackgroundHighlightText = moneyGreen[800];
export const tableRowHeaderBackground = moneyGreen[50];
export const tableRowHeaderText = moneyGreen[900];

// Sidebar - Deep green for a rich, financial feel
export const sidebarBackground = '#0d1f0d';
export const sidebarItemBackgroundPending = colorPalette.orange200;
export const sidebarItemBackgroundPositive = moneyGreen[500];
export const sidebarItemBackgroundFailed = colorPalette.red300;
export const sidebarItemBackgroundHover = '#1a3a1a';
export const sidebarItemAccentSelected = moneyGreen[400];
export const sidebarItemText = moneyGreen[100];
export const sidebarItemTextSelected = moneyGreen[300];
export const sidebarBudgetName = moneyGreen[100];

// Menus
export const menuBackground = colorPalette.white;
export const menuItemBackground = moneyGreen[50];
export const menuItemBackgroundHover = moneyGreen[100];
export const menuItemText = moneyGreen[900];
export const menuItemTextHover = menuItemText;
export const menuItemTextSelected = moneyGreen[600];
export const menuItemTextHeader = moneyGreen[500];
export const menuBorder = moneyGreen[100];
export const menuBorderHover = moneyGreen[200];
export const menuKeybindingText = moneyGreen[400];
export const menuAutoCompleteBackground = moneyGreen[900];
export const menuAutoCompleteBackgroundHover = moneyGreen[700];
export const menuAutoCompleteText = colorPalette.white;
export const menuAutoCompleteTextHover = moneyGreen[200];
export const menuAutoCompleteTextHeader = colorPalette.orange150;
export const menuAutoCompleteItemTextHover = menuAutoCompleteText;
export const menuAutoCompleteItemText = menuAutoCompleteText;

// Modals
export const modalBackground = colorPalette.white;
export const modalBorder = colorPalette.white;

// Mobile
export const mobileHeaderBackground = moneyGreen[600];
export const mobileHeaderText = moneyGreen[50];
export const mobileHeaderTextSubdued = moneyGreen[200];
export const mobileHeaderTextHover = 'rgba(200, 200, 200, .15)';
export const mobilePageBackground = moneyGreen[50];
export const mobileNavBackground = colorPalette.white;
export const mobileNavItem = colorPalette.gray300;
export const mobileNavItemSelected = moneyGreen[600];
export const mobileAccountShadow = moneyGreen[300];
export const mobileAccountText = moneyGreen[800];
export const mobileTransactionSelected = moneyGreen[500];
export const mobileViewTheme = mobileHeaderBackground;
export const mobileConfigServerViewTheme = moneyGreen[700];

// Markdown
export const markdownNormal = moneyGreen[150];
export const markdownDark = moneyGreen[600];
export const markdownLight = moneyGreen[100];

// Buttons - Menu style
export const buttonMenuText = moneyGreen[100];
export const buttonMenuTextHover = moneyGreen[50];
export const buttonMenuBackground = 'transparent';
export const buttonMenuBackgroundHover = 'rgba(200, 200, 200, .25)';
export const buttonMenuBorder = moneyGreen[500];
export const buttonMenuSelectedText = moneyGreen[800];
export const buttonMenuSelectedTextHover = moneyGreen[900];
export const buttonMenuSelectedBackground = moneyGreen[200];
export const buttonMenuSelectedBackgroundHover = moneyGreen[300];
export const buttonMenuSelectedBorder = buttonMenuSelectedBackground;

// Buttons - Primary (Money Green)
export const buttonPrimaryText = colorPalette.white;
export const buttonPrimaryTextHover = buttonPrimaryText;
export const buttonPrimaryBackground = moneyGreen[600];
export const buttonPrimaryBackgroundHover = moneyGreen[500];
export const buttonPrimaryBorder = buttonPrimaryBackground;
export const buttonPrimaryShadow = 'rgba(0, 0, 0, 0.3)';
export const buttonPrimaryDisabledText = colorPalette.white;
export const buttonPrimaryDisabledBackground = moneyGreen[300];
export const buttonPrimaryDisabledBorder = buttonPrimaryDisabledBackground;

// Buttons - Normal
export const buttonNormalText = moneyGreen[900];
export const buttonNormalTextHover = buttonNormalText;
export const buttonNormalBackground = colorPalette.white;
export const buttonNormalBackgroundHover = buttonNormalBackground;
export const buttonNormalBorder = moneyGreen[200];
export const buttonNormalShadow = 'rgba(0, 0, 0, 0.2)';
export const buttonNormalSelectedText = colorPalette.white;
export const buttonNormalSelectedBackground = moneyGreen[600];
export const buttonNormalDisabledText = moneyGreen[300];
export const buttonNormalDisabledBackground = buttonNormalBackground;
export const buttonNormalDisabledBorder = buttonNormalBorder;

// Calendar
export const calendarText = moneyGreen[50];
export const calendarBackground = moneyGreen[900];
export const calendarItemText = moneyGreen[150];
export const calendarItemBackground = moneyGreen[800];
export const calendarSelectedBackground = moneyGreen[600];

// Buttons - Bare
export const buttonBareText = buttonNormalText;
export const buttonBareTextHover = buttonNormalText;
export const buttonBareBackground = 'transparent';
export const buttonBareBackgroundHover = 'rgba(22, 163, 74, .15)';
export const buttonBareBackgroundActive = 'rgba(22, 163, 74, .25)';
export const buttonBareDisabledText = buttonNormalDisabledText;
export const buttonBareDisabledBackground = buttonBareBackground;

// Notice/Success states
export const noticeBackground = moneyGreen[150];
export const noticeBackgroundLight = moneyGreen[100];
export const noticeBackgroundDark = moneyGreen[500];
export const noticeText = moneyGreen[700];
export const noticeTextLight = moneyGreen[500];
export const noticeTextDark = moneyGreen[900];
export const noticeTextMenu = moneyGreen[200];
export const noticeBorder = moneyGreen[500];

// Warning states (keep orange for contrast)
export const warningBackground = colorPalette.orange200;
export const warningText = colorPalette.orange700;
export const warningTextLight = colorPalette.orange500;
export const warningTextDark = colorPalette.orange900;
export const warningBorder = colorPalette.orange500;

// Error states (keep red for clarity)
export const errorBackground = colorPalette.red100;
export const errorText = colorPalette.red500;
export const errorTextDark = colorPalette.red700;
export const errorTextDarker = colorPalette.red900;
export const errorTextMenu = colorPalette.red200;
export const errorBorder = colorPalette.red500;

// Upcoming/scheduled
export const upcomingBackground = moneyTeal[100];
export const upcomingText = moneyTeal[700];
export const upcomingBorder = moneyTeal[500];

// Forms
export const formLabelText = moneyGreen[700];
export const formLabelBackground = moneyGreen[200];
export const formInputBackground = moneyGreen[50];
export const formInputBackgroundSelected = colorPalette.white;
export const formInputBackgroundSelection = moneyGreen[500];
export const formInputBorder = moneyGreen[200];
export const formInputTextReadOnlySelection = moneyGreen[50];
export const formInputBorderSelected = moneyGreen[500];
export const formInputText = moneyGreen[900];
export const formInputTextSelected = moneyGreen[50];
export const formInputTextPlaceholder = moneyGreen[400];
export const formInputTextPlaceholderSelected = moneyGreen[300];
export const formInputTextSelection = moneyGreen[100];
export const formInputShadowSelected = moneyGreen[400];
export const formInputTextHighlight = moneyGreen[300];

// Checkboxes
export const checkboxText = tableBackground;
export const checkboxBackgroundSelected = moneyGreen[600];
export const checkboxBorderSelected = moneyGreen[600];
export const checkboxShadowSelected = moneyGreen[400];
export const checkboxToggleBackground = colorPalette.gray400;
export const checkboxToggleBackgroundSelected = moneyGreen[600];
export const checkboxToggleDisabled = colorPalette.gray200;

// Pills/Tags
export const pillBackground = moneyGreen[150];
export const pillBackgroundLight = moneyGreen[50];
export const pillText = moneyGreen[800];
export const pillTextHighlighted = moneyGreen[600];
export const pillBorder = moneyGreen[200];
export const pillBorderDark = moneyGreen[400];
export const pillBackgroundSelected = moneyGreen[200];
export const pillTextSelected = moneyGreen[900];
export const pillBorderSelected = moneyGreen[500];
export const pillTextSubdued = moneyGreen[300];

// Reports/Charts
export const reportsRed = colorPalette.red300;
export const reportsBlue = colorPalette.blue400;
export const reportsGreen = moneyGreen[400];
export const reportsGray = colorPalette.gray400;
export const reportsLabel = moneyGreen[900];
export const reportsInnerLabel = moneyGreen[800];

// Notes/Tags
export const noteTagBackground = moneyGreen[150];
export const noteTagBackgroundHover = moneyGreen[200];
export const noteTagDefault = moneyGreen[150];
export const noteTagText = colorPalette.black;

// Budget view
export const budgetCurrentMonth = tableBackground;
export const budgetOtherMonth = moneyGreen[50];
export const budgetHeaderCurrentMonth = budgetOtherMonth;
export const budgetHeaderOtherMonth = moneyGreen[100];

// Floating action bar
export const floatingActionBarBackground = moneyGreen[600];
export const floatingActionBarBorder = floatingActionBarBackground;
export const floatingActionBarText = moneyGreen[50];

// Tooltips
export const tooltipText = moneyGreen[900];
export const tooltipBackground = colorPalette.white;
export const tooltipBorder = moneyGreen[200];

// Calendar cells
export const calendarCellBackground = moneyGreen[100];

// Overlay
export const overlayBackground = 'rgba(0, 0, 0, 0.3)';
